import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import {
  CustomerCollectedDataStatus,
  CustomerCollectedDataTypeExtended,
} from 'src/constants/customer/enums'
import dayjs from 'dayjs'
import { GSIcon } from 'src/components/shared/GSIcon'
import {
  ContractorStepDetailsCard,
  ContractorStepDetailsCardUsageVariant,
} from 'src/components/contractorStepDetailsCard/ContractorStepDetailsCard'
import { ContractorStepDetailsCardStatus } from 'src/components/contractorStepDetailsCard/types'
import { mapMCStatusToCardStatus } from 'src/components/contractorStepDetailsCard/utils'

export interface MCStepParams {
  stepStatus: CustomerCollectedDataStatus
  stepType: CustomerCollectedDataTypeExtended
  expiresAt?: string
  onResubmitInfo?: () => void
  onChangeExpirationDate?: () => void
  onViewDetails?: () => void
}

interface Props {
  stepStatus: CustomerCollectedDataStatus
  stepType: CustomerCollectedDataTypeExtended
  expiresAt?: string
  onChangeExpirationDate?: (stepType: CustomerCollectedDataTypeExtended) => void
  onViewDetails?: (stepType: CustomerCollectedDataTypeExtended) => void
  onResubmitInfo?: (stepType: CustomerCollectedDataTypeExtended) => void
}

const MCStepItemComponent = ({
  stepStatus,
  stepType,
  expiresAt,
  onChangeExpirationDate,
  onViewDetails,
  onResubmitInfo,
}: Props) => {
  const handleChangeExpirationDate = useCallback(() => {
    onChangeExpirationDate?.(stepType)
  }, [stepType, onChangeExpirationDate])

  const handleViewDetails = useCallback(() => {
    onViewDetails?.(stepType)
  }, [stepType, onViewDetails])

  const handleResubmitInfo = useCallback(() => {
    onResubmitInfo?.(stepType)
  }, [stepType, onResubmitInfo])

  const baseCardStatus = mapMCStatusToCardStatus(stepStatus)
  const expiresSoon =
    !!expiresAt && dayjs(expiresAt).diff(dayjs(), 'day') <= 30 && dayjs(expiresAt).isAfter(dayjs())
  const cardStatus = expiresSoon ? ContractorStepDetailsCardStatus.ExpiringSoon : baseCardStatus

  const submissionActions =
    onViewDetails || onChangeExpirationDate ? (
      <div css={tw`flex flex-row items-center gap-2`}>
        {onChangeExpirationDate && (
          <Button
            iconOnly
            rightIcon={<GSIcon iconName="gs_calendar_edit" />}
            onClick={handleChangeExpirationDate}
            size="small"
            color="grey"
          />
        )}
        {onViewDetails && (
          <Button
            iconOnly
            rightIcon={<GSIcon iconName="gs_eye_open" />}
            onClick={handleViewDetails}
            size="small"
            color="white"
          />
        )}
      </div>
    ) : undefined

  return (
    <ContractorStepDetailsCard
      variant={ContractorStepDetailsCardUsageVariant.MasterContractor}
      stepType={stepType}
      status={cardStatus}
      expiresAt={expiresAt}
      actions={submissionActions}
      children={
        onResubmitInfo ? (
          <Button
            rightIcon={
              stepStatus !== CustomerCollectedDataStatus.Created ? (
                <GSIcon iconName="gs_arrows_repeat_right_left" />
              ) : undefined
            }
            onClick={handleResubmitInfo}
            css={tw`w-full`}
            size="small"
            color={stepStatus !== CustomerCollectedDataStatus.Created ? 'grey' : 'primary'}
          >
            {stepStatus !== CustomerCollectedDataStatus.Created ? 'Resubmit Info' : 'Submit Info'}
          </Button>
        ) : undefined
      }
    />
  )
}

export const MCStepItem = memo(MCStepItemComponent)
